<template>
  <Obj :id="id" :value="value">
    <Page
      v-if="value"
      ref="component"
      :value="value"
      :embedded-mode="embeddedMode"
    />
  </Obj>
</template>

<script lang="ts">
import { defineComponent, onMounted, onUpdated, toRefs, watch } from "vue";
import { useRoute } from "vue-router";

import type { Document, Page } from "@ctrl/controller-rpc";

import * as inject from "@/components/inject";
import Component from "@/components/page/Page.vue";
import { bindAsync, releasableRef, useReleaser } from "@/ref";

import Obj from "./DefaultObject.vue";
import { useObject } from "./object";

export default defineComponent({
  components: {
    Obj,
    Page: Component,
  },
  props: {
    id: { type: String, required: true },
    embeddedMode: {
      type: Boolean,
      default: false,
    },
  },
  async setup(props) {
    const { id } = toRefs(props);
    const route = useRoute();
    const controller = inject.controller();
    const {
      initializeComponentFocus,
      installRouteCallbacks,
      release,
      setObjectValue,
      ...object
    } = useObject(route, (value: Page) => {
      return [
        watch(
          () => value?.title || null,
          (title: string | null) => {
            if (title !== null)
              controller.page.update(value.id, { title }, "app");
          }
        ),
        watch(
          () => value?.notes || null,
          (content: Document | null) => {
            if (content !== null)
              controller.page.update(value.id, { notes: content }, "app");
          }
        ),
      ];
    });
    installRouteCallbacks();
    onMounted(initializeComponentFocus);
    onUpdated(initializeComponentFocus);
    const releaser = useReleaser();
    releaser.defer(release);
    const page = await releaser.add(
      bindAsync(id, async (id) => {
        const p = id ? await controller.page.get(id) : releasableRef(null);
        return p.map((p) =>
          p
            ? {
                ...p,
                notes: p.notes || { blocks: [] },
              }
            : p
        );
      })
    );
    setObjectValue(page);
    return object;
  },
});
</script>
